/**
 * Canonical standard GST rate definitions and display helpers.
 *
 * WHY A SHARED MODULE
 * -------------------
 * Before this module, "the standard GST rates" lived in three places:
 *
 *   - `ProductScreen.tsx` hard-coded them inside a `useEffect` that seeds the
 *     database. Those definitions were also missing the 0 % (Exempt) slab that
 *     `CreateInvoiceScreen` and `ProductEditorDrawer` display.
 *   - `QuotationEditorScreen.tsx` had its own `STANDARD_GST_RATES` tuple, used as
 *     a fallback when a business had no rates in the database yet.
 *   - `CreateInvoiceScreen.tsx` hard-coded the same five `<option>` values inline.
 *
 * Three copies drift. Centralising them here means every screen renders the same
 * set and the same labels, and the seeding effect and the fallback dropdown agree
 * on what "standard" means.
 */

import { formatRateBp } from '@core/money/format';

/** One standard GST slab. */
export interface StandardGstRate {
  /** Basis points. 18 % = 1800. */
  readonly rateBp: number;
  /** Short label for the database row (e.g. "GST 18%"). */
  readonly name: string;
  /** Whether this rate is the default selection for new products. */
  readonly isDefault: boolean;
}

/**
 * The five Indian GST slabs, in ascending order.
 *
 * 0 % is "Exempt" (nil-rated supply — no tax, but recorded).
 * 5 %, 12 %, 18 %, 28 % are the standard rates.
 *
 * These are the ONLY rates this application creates by default and the ONLY rates
 * a rate dropdown offers as a fallback. Business-specific rates (added by the user
 * or retained from existing data) are still read from the `taxRates` table first.
 */
export const STANDARD_GST_RATES: readonly StandardGstRate[] = [
  { rateBp: 0, name: 'Exempt', isDefault: false },
  { rateBp: 500, name: 'GST 5%', isDefault: false },
  { rateBp: 1200, name: 'GST 12%', isDefault: false },
  { rateBp: 1800, name: 'GST 18%', isDefault: true },
  { rateBp: 2800, name: 'GST 28%', isDefault: false },
] as const;

/**
 * The rateBp values extracted from {@link STANDARD_GST_RATES}, cheapest first.
 * Useful for diffing against stored rates.
 */
export const STANDARD_GST_RATE_BPS: readonly number[] = STANDARD_GST_RATES.map((r) => r.rateBp);

/**
 * Format a `rateBp` as a plain percentage for display (e.g. `1800` → `"18%"`).
 *
 * This is the canonical label format used by every GST dropdown and display in
 * the app: just the number followed by `%`. No redundant name prefix, no
 * parenthetical repetition.
 *
 * Use this instead of hand-rolling `formatRateBp(x) + '%'` in each component.
 */
export function formatGstRateLabel(rateBp: number): string {
  return `${formatRateBp(rateBp)}%`;
}
